import { TextCodes } from '@api/types/translation/TextCodes';
import { GOOGLE_MAP_PROPS } from '@components';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { InputAdornment, styled } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Coordinates } from '@store/location/location.slice';
import { t } from '@translations';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';

type PlacesAutocompleteProps = {
  setCoordinates: (coordinates: Coordinates | null) => void;
};

const StyledTextField = styled(TextField)`
  & .MuiInputBase-input {
    box-sizing: border-box;
  }
  & legend span {
    display: none;
  }
`;

export const PlacesAutocomplete = ({ setCoordinates }: PlacesAutocompleteProps) => {
  const {
    suggestions: { data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    debounce: 300,
    requestOptions: {
      types: ['(regions)'],
      componentRestrictions: { country: 'NO' },
      bounds: GOOGLE_MAP_PROPS.bounds,
    },
  });

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleSelect = (address: string) => {
    if (address) {
      setValue(address, false);
      clearSuggestions();

      getGeocode({ address })
        .then((results) => {
          if (results && results.length > 0) {
            const { lat, lng } = results[0].geometry.location;
            setCoordinates({ latitude: lat(), longitude: lng() });
          }
        })
        .catch((error) => {
          console.error('Error: ', error);
        });
    }
  };

  return (
    <Autocomplete
      id='places-autocomplete'
      sx={{ width: '100%' }}
      disablePortal
      getOptionLabel={(option) => option.description || ''}
      filterOptions={(x) => x}
      options={data}
      noOptionsText={t(TextCodes.Base.NoResults)}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position='start'>
                <LocationOnIcon />
              </InputAdornment>
            ),
          }}
          label={t(TextCodes.ProductRequest.Order.FindNearestDealer)}
          variant='outlined'
          fullWidth
          onChange={handleInput}
          placeholder={t(TextCodes.ProductRequest.Order.EnterZipCodeOrCity)}
        />
      )}
      isOptionEqualToValue={(option, value) => option.place_id === value.place_id}
      onInputChange={(event, newValue) => {
        setValue(newValue);
        if (!newValue) {
          setCoordinates(null);
        }
      }}
      onChange={(event, newValue) => {
        handleSelect(newValue ? newValue.description : '');
      }}
    />
  );
};
